import { Language } from '@coolblue-development/constants';
import { freddyTheme } from '@coolblue-development/freddy';
import { CheckmarkCircled } from '@coolblue-development/icons';
import { AvailabilityState as ClientAvailabilityState } from '@coolblue-development/next-availability-state/client';
import { useFormatShortDate } from '@coolblue-development/next-formatting-utils/client';
import { AvailabilityState, StoreSchedules } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';

import getNextPickupOption from './utils/getNextPickupOption';

type Props = {
  availabilityState: AvailabilityState;
  isClosed: boolean;
  isAvailableOnline: boolean;
  openingTime: string;
  language: Language;
  openingHours: StoreSchedules[];
  releaseDate?: string;
  dateOverride?: string;
};

function StoreAvailabilityState({
  availabilityState,
  isClosed,
  isAvailableOnline,
  openingTime,
  releaseDate,
  dateOverride,
  language,
  openingHours
}: Props) {
  const { t } = useTranslation('webshop.productPage.storePickup.propositions');
  const formattedReleaseDate = useFormatShortDate(releaseDate ? new Date(releaseDate) : new Date());

  const date = dateOverride ? new Date(dateOverride) : new Date();

  const getAvailabilityStateProps = () => {
    switch (availabilityState) {
      case AvailabilityState.OnStock:
        return {
          description: isClosed ? t('nextDayPickup', { openingTime }) : t('directPickup'),
          promise: t('onStock'),
          state: {
            color: freddyTheme.color.font.sentimentPositiveContrast,
            icon: <CheckmarkCircled color={freddyTheme.color.icon.sentimentPositiveContrast} />
          }
        };
      case AvailabilityState.PreOrder:
        return {
          preOrderPromise: t('preOrderPickup', { openingTime, releaseDate: formattedReleaseDate }),
          promise: t('preOrder'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      case AvailabilityState.TemporaryOutOfStock: {
        if (!isAvailableOnline) {
          return {
            description: t('notAvailableForPickup'),
            promise: t('temporaryOutOfStock'),
            state: {
              color: freddyTheme.color.font.sentimentNegative
            }
          };
        }

        const nextPickupOption = getNextPickupOption(openingHours, date, language);

        if (!nextPickupOption) {
          return {
            description: t('notAvailableForPickup'),
            promise: t('temporaryOutOfStock'),
            state: {
              color: freddyTheme.color.font.sentimentNegative
            }
          };
        }

        const { isTomorrow, localizedDayName, pickupTime } = nextPickupOption;

        return {
          description: isTomorrow
            ? t('nextDayPickup', { openingTime: pickupTime })
            : t('storePickupAtDay', {
                pickupTime,
                pickupDay: localizedDayName
              }),
          promise: t('temporaryOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      }
      default:
        return {
          description: t('notAvailableForPickup'),
          promise: t('temporaryOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
    }
  };

  return <ClientAvailabilityState {...getAvailabilityStateProps()} />;
}

export default StoreAvailabilityState;
